import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBriefcase,
  faTimes,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // текущая страница
  const companiesPerPage = 20; // компаний на странице
  const { t } = useTranslation();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch("/api/get_companies.php");
        const data = await res.json();
        if (data.success && data.companies) {
          setCompanies(data.companies);
          setFilteredCompanies(data.companies);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const handleSearchClick = () => {
    const value = searchTerm.toLowerCase();
    if (value === "") {
      setFilteredCompanies(companies);
      setPage(1); // сброс на первую страницу
    } else {
      const filtered = companies.filter(
        (company) =>
          company.companyName?.toLowerCase().includes(value) ||
          company.id.toString().includes(value)
      );
      setFilteredCompanies(filtered);
      setPage(1); // сброс на первую страницу
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setFilteredCompanies(companies);
    setPage(1); // сброс на первую страницу
  };

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center h-[200px]">
        <p className="text-[#006674] font-semibold animate-pulse text-xl">
          {t("loading")}...
        </p>
      </div>
    );
  }

  // пагинация
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);
  const currentCompanies = filteredCompanies.slice(
    (page - 1) * companiesPerPage,
    page * companiesPerPage
  );

  return (
    <section className="w-full flex flex-col items-center opacity-0 animate-[showtitle_1s_forwards] p-6 gap-6">
      <div className="flex w-full max-md:flex-col justify-between items-center">
        <h1 className="text-4xl max-md:text-2xl opacity-0 animate-[showtitle_1s_forwards] font-bold uppercase text-white mb-6">
          {t("companies")}
        </h1>

        <div className="flex items-center max-sm:w-full bg-white rounded-full pr-2 py-1 shadow-lg">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("search_placeholder") || "Փնտրել..."}
            className="py-2 px-6 outline-none max-sm:w-full text-lg rounded-l-full"
          />
          {searchTerm && (
            <button
              onClick={handleClear}
              className="text-gray-500 hover:text-red-500 px-2 text-xl"
              title={t("clear") || "Очистить"}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
          <button
            onClick={handleSearchClick}
            className="w-[45px] h-[45px] flex justify-center items-center text-white bg-[#006674] hover:bg-[#005560] transition-all text-xl rounded-full"
            title={t("search") || "Փնտրել"}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
      </div>

      {currentCompanies.length === 0 ? (
        <p className="text-white">{t("no_results")}</p>
      ) : (
        <>
          <div className="w-full flex flex-wrap justify-start p-6 max-md:p-3 gap-6">
            {currentCompanies.map((company) => (
              <Link
                to={`/ViewCompanyPage/${company.id}`}
                key={company.id}
                className="w-[410px] max-md:w-full p-2 bg-white transition-all md:hover:scale-105 flex justify-start rounded-xl items-center shadow-lg"
              >
                <img
                  src={
                    company.companyLogo
                      ? `/api/uploads/${company.companyLogo}`
                      : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                  }
                  alt={company.companyName}
                  className="w-[100px] h-[100px] bg-gray-300 shadow-2xl rounded-xl object-contain"
                />
                <div className="p-4 flex flex-col items-start gap-2 text-center">
                  <h2 className="text-2xl max-sm:text-xl font-semibold text-[#006674]">
                    {company.companyName || "Անանուն ընկերություն"}
                  </h2>

                  <div className="flex items-center max-sm:items-start gap-2 text-gray-600">
                    <FontAwesomeIcon
                      className="text-[#006674] max-sm:translate-y-1"
                      icon={faBriefcase}
                    />
                    <span className="text-lg max-sm:text-sm text-left font-medium">
                      {company.vacancyCount
                        ? `${t("companyvacancies2") || "վականսիա"} ${
                            company.vacancyCount
                          }`
                        : t("no_vacancies") || "Վականսիաներ չկան"}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => page > 1 && setPage(page - 1)}
                disabled={page === 1}
                className={`px-4 py-2 rounded-lg ${
                  page === 1
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-[#006674] text-white hover:bg-[#00555a]"
                } text-white transition`}
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-white font-semibold">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => page < totalPages && setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-4 py-2 rounded-lg ${
                  page === totalPages
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-[#006674] text-white hover:bg-[#00555a]"
                } text-white transition`}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default Companies;
